import {
	Box,
	Stack,
	Typography,
	alpha,
	useTheme,
} from '@mui/material';
import {
	CheckCircleRounded,
	CropLandscapeOutlined,
	CropPortraitOutlined,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

export type PageOrientation = 'portrait' | 'landscape';

interface Props {
	value: PageOrientation;
	onChange: (orientation: PageOrientation) => void;
}

const PageOrientationCards = ({ value, onChange }: Props) => {
	const theme = useTheme();
	const { t } = useTranslation();
	const accent = theme.palette.primary.main;

	const optionCardSx = (selected: boolean) => ({
		flex: 1,
		p: 2.5,
		borderRadius: 2.5,
		border: '2px solid',
		borderColor: selected ? accent : 'divider',
		bgcolor: selected ? alpha(accent, 0.08) : 'background.paper',
		cursor: 'pointer',
		position: 'relative' as const,
		transition: 'all 0.2s ease',
		'&:hover': {
			borderColor: accent,
			bgcolor: alpha(accent, 0.06),
			transform: 'translateY(-2px)',
			boxShadow: theme.shadows[4],
		},
	});

	const select = (orientation: PageOrientation) => () => onChange(orientation);

	return (
		<Box>
			<Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
				{t('common:clientSituation.export.chooseOrientation')}
			</Typography>
			<Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
				<Box
					role="button"
					tabIndex={0}
					aria-pressed={value === 'portrait'}
					onClick={select('portrait')}
					onKeyDown={(event) => {
						if (event.key === 'Enter' || event.key === ' ') {
							event.preventDefault();
							onChange('portrait');
						}
					}}
					sx={optionCardSx(value === 'portrait')}
				>
					{value === 'portrait' && (
						<CheckCircleRounded
							sx={{ position: 'absolute', top: 10, right: 10, color: 'primary.main', fontSize: 22 }}
						/>
					)}
					<OrientationCue orientation="portrait" accent={accent} />
					<Stack direction="row" spacing={0.75} alignItems="center">
						<CropPortraitOutlined color="primary" />
						<Typography variant="subtitle1" fontWeight={700}>
							{t('common:clientSituation.export.portrait.title')}
						</Typography>
					</Stack>
					<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
						{t('common:clientSituation.export.portrait.description')}
					</Typography>
				</Box>
				<Box
					role="button"
					tabIndex={0}
					aria-pressed={value === 'landscape'}
					onClick={select('landscape')}
					onKeyDown={(event) => {
						if (event.key === 'Enter' || event.key === ' ') {
							event.preventDefault();
							onChange('landscape');
						}
					}}
					sx={optionCardSx(value === 'landscape')}
				>
					{value === 'landscape' && (
						<CheckCircleRounded
							sx={{ position: 'absolute', top: 10, right: 10, color: 'primary.main', fontSize: 22 }}
						/>
					)}
					<OrientationCue orientation="landscape" accent={accent} />
					<Stack direction="row" spacing={0.75} alignItems="center">
						<CropLandscapeOutlined color="primary" />
						<Typography variant="subtitle1" fontWeight={700}>
							{t('common:clientSituation.export.landscape.title')}
						</Typography>
					</Stack>
					<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
						{t('common:clientSituation.export.landscape.description')}
					</Typography>
				</Box>
			</Stack>
		</Box>
	);
};

const OrientationCue = ({
	orientation,
	accent,
}: {
	orientation: PageOrientation;
	accent: string;
}) => {
	const portrait = orientation === 'portrait';
	return (
		<Box
			aria-hidden
			sx={{
				width: portrait ? 52 : 92,
				height: portrait ? 74 : 56,
				borderRadius: 1,
				border: '1px solid',
				borderColor: alpha(accent, 0.4),
				bgcolor: 'background.paper',
				boxShadow: 1,
				mx: 'auto',
				mb: 1.5,
				p: 0.75,
				display: 'flex',
				flexDirection: 'column',
				gap: 0.45,
			}}
		>
			<Box sx={{ height: 5, width: '70%', borderRadius: 1, bgcolor: alpha(accent, 0.4) }} />
			{[0, 1, 2].map((i) => (
				<Box
					key={i}
					sx={{
						height: 3,
						width: i === 2 ? '55%' : '100%',
						borderRadius: 1,
						bgcolor: 'action.disabledBackground',
					}}
				/>
			))}
		</Box>
	);
};

export default PageOrientationCards;
